/**
 * Cura — Bestsellers Grid
 * - Whole card click → product page (except variant pills, stepper, ATC).
 * - Variant pills update selected id + price/compare/discount.
 * - Stepper adjusts quantity.
 * - Add to cart → AJAX /cart/add, then open Dawn's cart drawer.
 * - Hides cells beyond each breakpoint's rows×cols total.
 */
(function () {
  'use strict';

  function money(cents) {
    // Uses the shop's money format if available, else a simple fallback.
    if (window.Shopify && window.Shopify.formatMoney && window.theme && window.theme.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    return '₹' + (cents / 100).toLocaleString('en-IN');
  }

  class CuraBestsellers {
    constructor(section) {
      this.section = section;
      this.applyBreakpointLimits();
      this.bindCards();
      window.addEventListener('resize', this.debounce(() => this.applyBreakpointLimits(), 150));
    }

    debounce(fn, wait) {
      var t;
      return function () { clearTimeout(t); t = setTimeout(fn, wait); };
    }

    cssNum(name, fallback) {
      const v = getComputedStyle(this.section).getPropertyValue(name).trim();
      const n = parseInt(v, 10);
      return isNaN(n) ? fallback : n;
    }

    // Show only the first N cells for the current breakpoint.
    applyBreakpointLimits() {
      const w = window.innerWidth;
      let count;
      if (w >= 990) count = this.cssNum('--cura-bsg-d-count', 8);
      else if (w >= 750) count = this.cssNum('--cura-bsg-t-count', 9);
      else count = this.cssNum('--cura-bsg-m-count', 8);

      this.section.querySelectorAll('.cura-bsg__cell').forEach((cell, i) => {
        cell.style.display = i < count ? 'flex' : 'none';
      });
    }

    bindCards() {
      this.section.querySelectorAll('[data-product-card]').forEach((card) => this.bindCard(card));
    }

    bindCard(card) {
      const url = card.dataset.productUrl;
      const variants = Array.from(card.querySelectorAll('[data-variant-id]'));
      const priceEl = card.querySelector('[data-price]');
      const compareEl = card.querySelector('[data-compare]');
      const discountEl = card.querySelector('[data-discount]');
      const atc = card.querySelector('[data-add-to-cart]');
      const qtyInput = card.querySelector('[data-qty]');

      // Whole-card click → product page, except interactive controls.
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-variants], [data-stepper], [data-add-to-cart], a')) return;
        window.location.href = url;
      });

      // Variant selection
      variants.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          variants.forEach((b) => {
            b.classList.remove('is-selected');
            b.setAttribute('aria-checked', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-checked', 'true');

          const price = parseInt(btn.dataset.variantPrice, 10);
          const compare = parseInt(btn.dataset.variantCompare, 10) || 0;
          // Use the server-formatted price strings (same format as initial render)
          // instead of reformatting in JS, so the currency display never changes.
          if (priceEl) priceEl.textContent = btn.dataset.variantPriceFormatted || priceEl.textContent;

          const onSale = compare > price;
          if (compareEl) {
            compareEl.hidden = !onSale;
            if (onSale) compareEl.textContent = btn.dataset.variantCompareFormatted || '';
          }

          if (atc) {
            atc.dataset.variantId = btn.dataset.variantId;
            const available = btn.dataset.variantAvailable === 'true';
            atc.disabled = !available;
            atc.querySelector('.cura-bsg-card__atc-label').textContent = available ? 'Add to cart' : 'Sold out';
          }
        });
      });

      // Stepper
      const stepper = card.querySelector('[data-stepper]');
      if (stepper) {
        stepper.addEventListener('click', (e) => e.stopPropagation());
        card.querySelector('[data-step-down]').addEventListener('click', () => {
          qtyInput.value = Math.max(1, parseInt(qtyInput.value, 10) - 1);
        });
        card.querySelector('[data-step-up]').addEventListener('click', () => {
          qtyInput.value = parseInt(qtyInput.value, 10) + 1;
        });
      }

      // Add to cart
      if (atc) {
        atc.addEventListener('click', (e) => {
          e.stopPropagation();
          this.addToCart(atc, qtyInput);
        });
      }
    }

    addToCart(atc, qtyInput) {
      const id = atc.dataset.variantId;
      const qty = Math.max(1, parseInt(qtyInput ? qtyInput.value : '1', 10));
      atc.classList.add('is-loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: qty }] })
      })
        .then((r) => r.json())
        .then(() => this.refreshAndOpenCart())
        .catch(() => { /* fail silently; could surface an error toast */ })
        .finally(() => atc.classList.remove('is-loading'));
    }

    // Update Dawn's cart drawer and open it.
    refreshAndOpenCart() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        // Fetch updated cart sections so the drawer shows the new item.
        fetch(window.routes && window.routes.cart_url ? window.routes.cart_url + '?section_id=cart-drawer' : '/?section_id=cart-drawer')
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newDrawer = doc.querySelector('cart-drawer');
            if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;
            if (typeof drawer.open === 'function') drawer.open();
            else drawer.classList.add('active', 'animate', 'is-empty');
            document.body.classList.add('overflow-hidden');
          });
      } else {
        // No drawer (cart page mode): go to cart.
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
    }
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('.cura-bsg').forEach(function (el) {
      if (!el.dataset.bsgInit) {
        new CuraBestsellers(el);
        el.dataset.bsgInit = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });

  // Re-bind product cards inside an arbitrary container (e.g. AJAX-swapped
  // content in the Find-your-solution tabs). Reuses CuraBestsellers' card
  // logic without the grid/breakpoint behavior, which doesn't apply there.
  window.CuraBestsellersInit = function (container) {
    var scope = container && container.querySelectorAll ? container : document;

    // First, handle any full .cura-bsg sections in the scope (normal path).
    init(scope);

    // Then bind loose cards not inside a .cura-bsg section.
    var helper = Object.create(CuraBestsellers.prototype);
    helper.section = scope;
    scope.querySelectorAll('[data-product-card]').forEach(function (card) {
      if (card.dataset.cardBound) return;
      if (card.closest('.cura-bsg')) return; // already handled by init()
      card.dataset.cardBound = 'true';
      helper.bindCard(card);
    });
  };
})();